import { FC, useReducer, useState } from 'react';

import { FolderPen, FolderPlus, Settings, Trash2, Upload } from 'lucide-react';
import { isNonNullish } from 'remeda';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';

import { FolderEditDialog } from './folderEditDialog/FolderEditDialog';
import { NestedFolderDialog } from './nestedFolderDialog/NestedFolderDialog';
import { UploadFilesDialog } from './uploadFilesDialog/UploadFilesDialog';

import css from './FolderActionsColumn.module.css';

import { fileSystemService } from 'v2/api/fileSystemService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { Nullable } from 'v2/types';
import { FolderModel } from 'v2/types/FileSystemModel';
import { handleHttpError } from 'v2/utils';

interface FolderActionsColumnProps {
  folder: FolderModel;
  onRevalidate?: VoidFunction;
}

export const FolderActionsColumn: FC<FolderActionsColumnProps> = ({ folder, onRevalidate }) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const [isOpenEditDialog, setOpenEditDialog] = useReducer((state) => !state, false);
  const [isOpenUploadFilesDialog, setOpenUploadFilesDialog] = useReducer((state) => !state, false);
  const [isOpenNestedFolderDialog, setOpenNestedFolderDialog] = useReducer((state) => !state, false);

  const { trigger } = useSWRMutation(['deleteFolder', folder.id], fileSystemService.deleteFolder, {
    onSuccess: (newFolder) => {
      handleRevalidateFolders(newFolder.isRoot, folder.parentId);
    },
    onError: handleHttpError,
  });

  const handleRevalidateFolders = (isRoot: boolean, folderId?: Nullable<string>) => {
    if (isNonNullish(onRevalidate)) {
      onRevalidate();
    } else {
      if (isRoot) {
        mutate('getRootFolders');
      } else {
        mutate(['getChildFolders', folderId]);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseMenuItem = (cb: VoidFunction) => () => {
    handleClose();
    cb();
  };

  return (
    <TableCell align="right">
      <div className={css.folderActions}>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Settings width={22} height={22} />
        </IconButton>

        <Popconfirm message="Are you sure that you want to delete this folder?" onConfirm={trigger}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={22} height={22} />
            </IconButton>
          </Tooltip>
        </Popconfirm>
      </div>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem className={css.folderActionsMenuItem} onClick={handleCloseMenuItem(setOpenUploadFilesDialog)}>
          <Upload width={17} height={17} /> Upload Files
        </MenuItem>

        <Divider />

        <MenuItem className={css.folderActionsMenuItem} onClick={handleCloseMenuItem(setOpenEditDialog)}>
          <FolderPen width={18} height={18} /> Edit Folder
        </MenuItem>

        <Divider />

        <MenuItem className={css.folderActionsMenuItem} onClick={handleCloseMenuItem(setOpenNestedFolderDialog)}>
          <FolderPlus width={18} height={18} /> Add Folder
        </MenuItem>
      </Menu>

      <FolderEditDialog
        folder={folder}
        isOpen={isOpenEditDialog}
        onClose={setOpenEditDialog}
        onSuccess={handleRevalidateFolders}
      />

      <UploadFilesDialog folder={folder} isOpen={isOpenUploadFilesDialog} onClose={setOpenUploadFilesDialog} />

      <NestedFolderDialog
        folder={folder}
        isOpen={isOpenNestedFolderDialog}
        onClose={setOpenNestedFolderDialog}
        onSuccess={handleRevalidateFolders}
      />
    </TableCell>
  );
};
